/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  accessAPI,
  tituloBase,
  Titulo,
  FormularioCampo,
  Boton,
} from "../../../shared/index.js";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { ConfirmDialog } from "primereact/confirmdialog";
import CardDescuentos from "./CardDescuentos.js";

/**
 * Sección de configuración del cliente. Reemplaza al sidebar que se
 * abría desde el botón "Configurar cliente" en la vista de detalle.
 * Reutiliza el endpoint GET /admin/cliente/:id para cargar los flags
 * y PUT /admin/cliente/:id para actualizarlos (un campo a la vez).
 */
export default function ClienteConfiguracion() {
  const [cliente, setCliente] = useState();
  const [opcionesClientes, setOpcionesClientes] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const toast = useRef(null);
  const { clienteId } = useParams();

  useEffect(() => {
    if (!clienteId) return;
    accessAPI(
      "GET",
      `admin/cliente/${clienteId}`,
      null,
      (respuesta) => {
        setCliente(respuesta);
        document.title = `Configuración - ${respuesta.razonsocial}`;
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0].msg,
          sticky: true
        });
      }
    );
    accessAPI(
      "GET",
      "admin/cliente",
      null,
      (respuesta) => {
        setOpcionesClientes([
          { value: -1, etiqueta: "Ninguna" },
          ...respuesta
        ]);
      },
      () => {}
    );
    return () => (document.title = tituloBase());
  }, [clienteId]);

  function actualizarConfiguracion(campo, valor) {
    // Actualización optimista: aplicar el nuevo valor de inmediato para que
    // el switch refleje el estado nuevo mientras el request está en vuelo.
    // Si la API falla, se revierte al valor anterior.
    const valorAnterior = cliente[campo];
    setCliente({ ...cliente, [campo]: valor });
    setGuardando(true);
    accessAPI(
      "PUT",
      `admin/cliente/${cliente.id}`,
      { [campo]: valor },
      () => {
        setGuardando(false);
      },
      (respuesta) => {
        setCliente((prev) => ({ ...prev, [campo]: valorAnterior }));
        setGuardando(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0].msg,
          sticky: true
        });
      }
    );
  }

  function handleFacturarAChange(_campo, valor) {
    const valorActual = cliente.facturara?.id ?? -1;
    if (valor === valorActual) return;

    const clienteIdFacturar = valor === -1 ? null : valor;
    const facturaraAnterior = cliente.facturara;
    const clienteSeleccionado = opcionesClientes?.find(
      (c) => c.value === valor
    );

    // Actualización optimista del dropdown.
    setCliente({
      ...cliente,
      facturara: clienteIdFacturar
        ? { id: valor, razonsocial: clienteSeleccionado?.etiqueta }
        : null
    });
    setGuardando(true);
    accessAPI(
      "PUT",
      `admin/cliente/${cliente.id}`,
      { facturarAClienteId: clienteIdFacturar },
      () => {
        setGuardando(false);
      },
      (respuesta) => {
        setCliente((prev) => ({ ...prev, facturara: facturaraAnterior }));
        setGuardando(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0].msg,
          sticky: true
        });
      }
    );
  }

  if (!cliente) {
    return (
      <>
        <Toast ref={toast} position="top-center" />
        <Skeleton
          width="20rem"
          height="2rem"
          css={{ marginTop: "2em", marginBottom: "1em" }}
        />
        <div className="grid">
          <div className="col-12 lg:col-6">
            <Card>
              <Skeleton
                width="12rem"
                height="1.5rem"
                css={{ marginBottom: "1.5em" }}
              />
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5em"
                }}
              >
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} width="100%" height="1.75rem" />
                ))}
              </div>
            </Card>
          </div>
          <div
            className="col-12 lg:col-6"
            css={{ display: "flex", flexDirection: "column", gap: "1em" }}
          >
            <Card>
              <Skeleton
                width="10rem"
                height="1.5rem"
                css={{ marginBottom: "1.5em" }}
              />
              <Skeleton
                width="8rem"
                height="1rem"
                css={{ marginBottom: "0.75em" }}
              />
              <Skeleton width="100%" height="2.5rem" />
            </Card>
            <Card>
              <Skeleton
                width="10rem"
                height="1.5rem"
                css={{ marginBottom: "1.5em" }}
              />
              <Skeleton
                width="100%"
                height="1.5rem"
                css={{ marginBottom: "0.75em" }}
              />
              <Skeleton width="100%" height="1.5rem" />
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Titulo
        botones={
          guardando ? (
            <div
              css={{
                display: "flex",
                alignItems: "center",
                gap: "0.5em",
                color: "var(--grisTextos)",
                fontSize: "0.9em"
              }}
            >
              <i className="pi pi-spin pi-spinner" />
              <span>Guardando...</span>
            </div>
          ) : null
        }
      >
        {cliente ? `${cliente.razonsocial} - Configuración` : "Configuración"}
      </Titulo>

      {cliente && (
        <div className="grid">
          <div className="col-12 lg:col-6">
            <Card>
              <Titulo>Facturación de juguetes</Titulo>
              <div
                css={{ display: "flex", flexDirection: "column", gap: "1.5em" }}
              >
                {/* El rubro del cliente ya no se prende acá: se deduce de sus
                    procesos activos. Quedan solo estos dos, que no son
                    clasificación sino condiciones comerciales, y por eso se
                    muestran siempre. */}
                <Boton
                  interruptor
                  interruptorActivo={cliente.pagamosensayosjuguetes}
                  disabled={guardando}
                  callback={(valor) =>
                    actualizarConfiguracion("pagamosensayosjuguetes", valor)
                  }
                >
                  Ensayos pagos por el organismo
                </Boton>
                <Boton
                  interruptor
                  interruptorActivo={cliente.aplicadescuentojuguetes}
                  disabled={guardando}
                  callback={(valor) =>
                    actualizarConfiguracion("aplicadescuentojuguetes", valor)
                  }
                >
                  Aplica descuento (AJU)
                </Boton>
              </div>
            </Card>
          </div>

          <div
            className="col-12 lg:col-6"
            css={{ display: "flex", flexDirection: "column", gap: "1em" }}
          >
            <Card>
              <Titulo>Facturación</Titulo>
              <div css={{ fontWeight: "600", marginBottom: "0.75em" }}>
                Facturar a otra empresa
              </div>
              {opcionesClientes && (
                <FormularioCampo
                  campo={{
                    nombre: "facturarAClienteId",
                    tipoitemformularioid: 5,
                    etiqueta: "Empresa",
                    opciones: opcionesClientes,
                    respuesta: cliente.facturara?.id ?? -1
                  }}
                  disabled={guardando}
                  onUpdate={handleFacturarAChange}
                />
              )}
              {!opcionesClientes && (
                <span css={{ color: "var(--text-color-secondary)" }}>
                  Cargando empresas...
                </span>
              )}
            </Card>

            <CardDescuentos clienteId={cliente.id} />
          </div>
        </div>
      )}
    </>
  );
}
